import React, { useEffect } from "react";
import { useFormikContext } from "formik";
import TextInput from "../../../../../../components/form/text-input";
import TextArea from "../../../../../../components/form/text-area";
import { Select } from "../../../../../../components/form/select";
import InfoIcon from "../../../../../../assets/svg/icons/common/info";
import FancyCheckbox from "../../../../../../components/form/fancy-checkbox";
import styles from "./style.module.scss";
import baseStyles from "../../style.module.scss";
import ReactTooltip from "react-tooltip";
import useDomainStore from "../../../../../../stores/domain";

const ToolTipVisaRisk =
  "Has your merchant account ever been terminated due to being listed in any of Visa's Risk Programs, such as Visa Fraud Monitoring, Visa Dispute Monitoring, or Common Point of Purchase?";
const ToolTipThirdPartyPayment =
  "Examples of third-party applications include accounting software, billing platforms, eCommerce systems, or customer relationship management (CRM) tools.";
const ToolTipProductDescription =
  "Write a short description of the products and services you sell. The underwriting team needs to fully understand your business model in order to properly deploy the an account, so please be as specific as you can.";

const multiLocationFieldGroups = [
  { label: "Multi-Merchant", currentField: "multi_merchant", totalField: "multi_merchant_of" },
  { label: "Additional Location", currentField: "additional_location", totalField: "additional_location_of" },
];

/**
 * Available advertising options for the business
 */
const advertisingOptions = [
  { label: "Online", value: "online" },
  { label: "TV", value: "tv" },
  { label: "Radio", value: "radio" },
  { label: "Other", value: "other" },
];

/**
 * BusinessProfile Component
 * Renders a form for collecting detailed business profile information including
 * products/services description, advertising methods, and compliance details
 */
const BusinessProfile = ({ disputeRecoveryVisible = false }) => {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
  } = useFormikContext();
  const { isKurvDomain } = useDomainStore();
  /**
   * Initialize default values for form fields on component mount
   */
  useEffect(() => {
    if (!values.prod_advertise_description)
      setFieldValue("prod_advertise_description", ["online"]);
    if (values.is_thirdparty_involved === undefined)
      setFieldValue("is_thirdparty_involved", "no");
    if (values.pci_complaint === undefined)
      setFieldValue("pci_complaint", "no");
    if (values.terminated_as_vmdax === undefined)
      setFieldValue("terminated_as_vmdax", "no");
    if (values.prior_data_compromise_investigation === undefined)
      setFieldValue("prior_data_compromise_investigation", "no");
    if (values.identified_visa_risk_program === undefined)
      setFieldValue("identified_visa_risk_program", "no");
    if (values.is_multiple_locations === undefined)
      setFieldValue("is_multiple_locations", "no");
  }, []);

  // Helper function to update field value and mark as touched
  const updateField = (field, value) => {
    setFieldValue(field, value);
    setFieldTouched(field, true, false); // Mark as touched but don't validate yet
  };

  // Answering "no" has to clear the counts, not just hide them. They are sent
  // unconditionally on submit and printed onto the signed application, so a
  // number typed before switching back would end up on the merchant's PDF.
  const setMultipleLocations = (value) => {
    updateField("is_multiple_locations", value);
    if (value === "no") {
      multiLocationFieldGroups.forEach(({ currentField, totalField }) => {
        setFieldValue(currentField, "");
        setFieldValue(totalField, "");
      });
    }
  };

  return (
    <div
      className={`${baseStyles.sectionContainer} ${isKurvDomain ? styles.kurv_theme : ""}`}
    >
      <ReactTooltip className={styles.tooltip} />

      <div className={baseStyles.sectionHeader}>
        <h4 className={baseStyles.sectionTitle}>Business Profile</h4>
      </div>
      <div className={baseStyles.sectionFormContent}>
        <div className={styles.formGroup}>
          <label className={styles.label}>
            Products and services description
            <span className={styles.required}>*</span>
          </label>
          <TextArea
            id="product_service_description"
            name="product_service_description"
            rows={3}
            value={values.product_service_description || ""}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.product_service_description}
            errored={errors.product_service_description}
            error={errors.product_service_description}
            hasInfoIcon
            infoIconTooltip={ToolTipProductDescription}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} id="advertising_methods_label">
            Select all ways that your products and/or services are advertised
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          </label>
          <div
            className={styles.checkboxGroup}
            role="group"
            aria-labelledby="advertising_methods_label"
          >
            {advertisingOptions.map((option) => (
              <FancyCheckbox
                className={styles.checkboxItem}
                key={option.value}
                id={`advertising_${option.value}`}
                label={option.label}
                name="prod_advertise_description"
                value={option.value}
                checked={values.prod_advertise_description?.includes(
                  option.value,
                )}
                onChange={(e) => {
                  const currentMethods =
                    values.prod_advertise_description || [];
                  if (e.target.checked) {
                    setFieldValue("prod_advertise_description", [
                      ...currentMethods,
                      option.value,
                    ]);
                  } else {
                    setFieldValue(
                      "prod_advertise_description",
                      currentMethods.filter(
                        (method) => method !== option.value,
                      ),
                    );
                  }
                  setFieldTouched("prod_advertise_description", true, false);
                }}
              />
            ))}
          </div>
          {touched.prod_advertise_description &&
            errors.prod_advertise_description && (
              <div className={styles.error} role="alert">
                {errors.prod_advertise_description}
              </div>
            )}
        </div>

        <div className={styles.item}>
          <label className={styles.label}>
            Are there 3rd party application involved with your payment process?
            <span className={styles.required}>*</span>
          </label>
          <div className={styles.horizontalLayout}>
            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={`${styles.button} ${values.is_thirdparty_involved === "yes" ? styles.selected : ""}`}
                onClick={() => updateField("is_thirdparty_involved", "yes")}
              >
                Yes
              </button>
              <button
                type="button"
                className={`${styles.button} ${values.is_thirdparty_involved === "no" ? styles.selected : ""}`}
                onClick={() => updateField("is_thirdparty_involved", "no")}
              >
                No
              </button>
            </div>

            <div
              className={`${styles.inlineConditionalInput} ${values.is_thirdparty_involved === "yes" ? styles.show : styles.hidden}`}
            >
              <TextInput
                placeholder="3rd party application involved"
                type="text"
                id="thirdparty_involved_description"
                name="thirdparty_involved_description"
                value={values.thirdparty_involved_description || ""}
                onChange={handleChange}
                touched={touched.thirdparty_involved_description}
                errored={
                  touched.thirdparty_involved_description &&
                  errors.thirdparty_involved_description
                }
                error={errors.thirdparty_involved_description}
                suffix={
                  <InfoIcon
                    data-tip={ToolTipThirdPartyPayment}
                    data-place="top"
                  />
                }
                style={{ margin: "0" }}
              />
            </div>
          </div>
          {touched.is_thirdparty_involved && errors.is_thirdparty_involved && (
            <div className={styles.error}>{errors.is_thirdparty_involved}</div>
          )}
        </div>

        <div className={styles.item}>
          <label className={styles.label}>
            Is your business PCI compliant?
            <span className={styles.required}>*</span>
          </label>
          <div className={styles.buttonGroup}>
            <button
              type="button"
              className={`${styles.button} ${values.pci_complaint === "yes" ? styles.selected : ""}`}
              onClick={() => updateField("pci_complaint", "yes")}
            >
              Yes
            </button>
            <button
              type="button"
              className={`${styles.button} ${values.pci_complaint === "no" ? styles.selected : ""}`}
              onClick={() => updateField("pci_complaint", "no")}
            >
              No
            </button>
          </div>
          {touched.pci_complaint && errors.pci_complaint && (
            <div className={styles.error}>{errors.pci_complaint}</div>
          )}
        </div>

        <div className={styles.item}>
          <label className={styles.label}>
            Has the business or any associated owner ever been terminated as a
            VISA/MasterCard/Discover/AMEX merchant?
            <span className={styles.required}>*</span>
          </label>
          <div className={styles.buttonGroup}>
            <button
              type="button"
              className={`${styles.button} ${values.terminated_as_vmdax === "yes" ? styles.selected : ""}`}
              onClick={() => updateField("terminated_as_vmdax", "yes")}
            >
              Yes
            </button>
            <button
              type="button"
              className={`${styles.button} ${values.terminated_as_vmdax === "no" ? styles.selected : ""}`}
              onClick={() => updateField("terminated_as_vmdax", "no")}
            >
              No
            </button>
          </div>
          {touched.terminated_as_vmdax && errors.terminated_as_vmdax && (
            <div className={styles.error}>{errors.terminated_as_vmdax}</div>
          )}
        </div>

        <div className={styles.item}>
          <label className={styles.label}>
            Has the business had any ongoing or prior data compromise
            investigations?
            <span className={styles.required}>*</span>
          </label>
          <div className={styles.buttonGroup}>
            <button
              type="button"
              className={`${styles.button} ${values.prior_data_compromise_investigation === "yes" ? styles.selected : ""}`}
              onClick={() =>
                updateField("prior_data_compromise_investigation", "yes")
              }
            >
              Yes
            </button>
            <button
              type="button"
              className={`${styles.button} ${values.prior_data_compromise_investigation === "no" ? styles.selected : ""}`}
              onClick={() =>
                updateField("prior_data_compromise_investigation", "no")
              }
            >
              No
            </button>
          </div>
          {touched.prior_data_compromise_investigation &&
            errors.prior_data_compromise_investigation && (
              <div className={styles.error}>
                {errors.prior_data_compromise_investigation}
              </div>
            )}
        </div>

        <div className={styles.item}>
          <label className={styles.label}>
            Has the business been previously identified with Visa Risk Programs?
            <span className={styles.required}>*</span>
          </label>
          <div className={styles.horizontalLayout}>
            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={`${styles.button} ${values.identified_visa_risk_program === "yes" ? styles.selected : ""}`}
                onClick={() =>
                  updateField("identified_visa_risk_program", "yes")
                }
              >
                Yes
              </button>
              <button
                type="button"
                className={`${styles.button} ${values.identified_visa_risk_program === "no" ? styles.selected : ""}`}
                onClick={() =>
                  updateField("identified_visa_risk_program", "no")
                }
              >
                No
              </button>
            </div>

            <div
              className={`${styles.inlineConditionalInput} ${values.identified_visa_risk_program === "yes" ? styles.show : styles.hidden}`}
            >
              <TextInput
                placeholder="Visa Risk Programs"
                type="text"
                id="visa_risk_program_desc"
                name="visa_risk_program_desc"
                value={values.visa_risk_program_desc || ""}
                onChange={handleChange}
                touched={touched.visa_risk_program_desc}
                errored={
                  touched.visa_risk_program_desc &&
                  errors.visa_risk_program_desc
                }
                error={errors.visa_risk_program_desc}
                suffix={
                  <InfoIcon data-tip={ToolTipVisaRisk} data-place="top" />
                }
              />
            </div>
          </div>
          {touched.identified_visa_risk_program &&
            errors.identified_visa_risk_program && (
              <div className={styles.error}>
                {errors.identified_visa_risk_program}
              </div>
            )}
        </div>

        <div className={styles.item}>
          <label className={styles.label}>
            Multiple locations?
            <span className={styles.required}>*</span>
          </label>
          <div className={styles.buttonGroup}>
            <button
              type="button"
              className={`${styles.button} ${values.is_multiple_locations === "yes" ? styles.selected : ""}`}
              onClick={() => setMultipleLocations("yes")}
            >
              Yes
            </button>
            <button
              type="button"
              className={`${styles.button} ${values.is_multiple_locations === "no" ? styles.selected : ""}`}
              onClick={() => setMultipleLocations("no")}
            >
              No
            </button>
          </div>
          {touched.is_multiple_locations && errors.is_multiple_locations && (
            <div className={styles.error}>{errors.is_multiple_locations}</div>
          )}

          {values.is_multiple_locations === "yes" && (
            <div className={styles.conditionalInput}>
              {multiLocationFieldGroups.map(({ label, currentField, totalField }) => (
                <div className={styles.multiLocationRow} key={currentField}>
                  <label className={styles.label}>{label}</label>
                  <div className={styles.pairedInputs}>
                    <TextInput
                      placeholder="#"
                      type="number"
                      id={currentField}
                      name={currentField}
                      value={values[currentField] || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched[currentField]}
                      errored={touched[currentField] && errors[currentField]}
                      error={errors[currentField]}
                    />
                    <span className={styles.ofText}>of</span>
                    <TextInput
                      placeholder="#"
                      type="number"
                      id={totalField}
                      name={totalField}
                      value={values[totalField] || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      touched={touched[totalField]}
                      errored={touched[totalField] && errors[totalField]}
                      error={errors[totalField]}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {disputeRecoveryVisible && (
          <div className={styles.item}>
            <label className={styles.label}>
              Dispute Recovery Program
            </label>
            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={`${styles.button} ${values.dispute_recovery_program === true ? styles.selected : ""}`}
                onClick={() => updateField("dispute_recovery_program", true)}
              >
                Opt In
              </button>
              <button
                type="button"
                className={`${styles.button} ${values.dispute_recovery_program === false ? styles.selected : ""}`}
                onClick={() => updateField("dispute_recovery_program", false)}
              >
                Opt Out
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessProfile;
